<template>
  <div class="column full-height no-wrap">
    <!-- TOP BAR -->
    <div class="column shrink shadow-2">
      <q-toolbar class="bg-grey-3 text-black">
        <q-btn flat dense icon="arrow_back" @click="previousBatter">
          <q-tooltip>Previous Batter</q-tooltip>
        </q-btn>
        <q-btn flat dense @click="openDefenseSheet" class="q-px-sm">
          <div
            class="row items-center no-wrap text-weight-bolder"
            style="font-size: 1.1rem; letter-spacing: 1px"
          >
            D<q-icon name="fence" size="sm" style="margin-left: -4px" />
          </div>
          <HelpHint text="Manage Defense" />
        </q-btn>
        <q-space />

        <!-- View Toggle (Removed toolbar-center wrapper) -->
        <div class="relative-position">
          <q-btn-toggle
            v-model="activeView"
            no-caps
            dense
            unelevated
            toggle-color="primary"
            :options="responsiveToggleOptions"
          />
          <HelpHint
            text="Switch between tracking pitches, hits, plays, game summaries and scouting."
            top="-8px"
            right="-8px"
          />
        </div>

        <q-space />
        <q-btn flat dense icon="build" @click="showAdjustmentSheet = true">
          <HelpHint text="Manually correct the score, inning, or current batter." />
        </q-btn>
        <q-btn
          flat
          dense
          icon-right="arrow_forward"
          @click="nextBatter"
          :disable="isLiveEdge && !gameStore.currentAbState(true).status"
        >
          <q-tooltip>Next Batter</q-tooltip>
        </q-btn>
      </q-toolbar>
      <GameAdjustmentSheet v-model="showAdjustmentSheet" />
    </div>

    <!-- MAIN TRACKING VIEWS -->
    <GameTrackerPitching
      ref="refPitchComponent"
      v-if="activeView === 'pitch'"
      class="col-grow"
      @switch-view="activeView = $event"
    />
    <GameTrackerHitting
      v-else-if="activeView === 'hit'"
      class="col-grow"
      @switch-view="activeView = $event"
    />
    <GameTrackerAction
      v-else-if="activeView === 'play'"
      class="col-grow"
      @switch-view="activeView = $event"
    />
    <GameTrackerScouting
      v-else-if="activeView === 'scout'"
      class="col-grow"
      @switch-view="activeView = $event"
    />
    <GameTrackerSummary
      v-else-if="activeView === 'summary'"
      class="col-grow"
      @switch-view="activeView = $event"
    />

    <!-- BOTTOM SHEET 1: DEFENSE OVERVIEW (DRAFT MODE) -->
    <q-dialog v-model="showDefenseSheet" position="top" persistent>
      <q-card
        class="full-width bg-white column no-wrap"
        style="max-height: 85vh; border-radius: 0 0 16px 16px; overflow: hidden"
      >
        <!-- Sticky Header -->
        <q-card-section
          class="row items-center justify-between col-auto bg-grey-2 border-bottom z-top"
        >
          <div class="text-subtitle1 text-weight-bold text-primary">Edit Defense</div>
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <!-- Scrollable List -->
        <q-card-section class="col scroll q-pa-none">
          <q-list separator>
            <q-item
              v-for="pos in 9"
              :key="'pos-' + pos"
              clickable
              v-ripple
              @click="openSwapMenu(pos)"
              class="q-py-md"
            >
              <q-item-section avatar class="q-pr-sm" style="min-width: 70px">
                <q-chip
                  color="primary"
                  text-color="white"
                  class="text-weight-bold shadow-1"
                  size="sm"
                >
                  {{ settingsStore.getPositionShortLabel(pos) }}
                </q-chip>
              </q-item-section>

              <q-item-section>
                <q-item-label
                  class="text-weight-bold text-body1"
                  :class="{ 'text-positive': isPositionChanged(pos) }"
                >
                  #{{ getDraftPlayerAtPosition(pos)?.uniformNumber || '-' }}
                </q-item-label>
                <q-item-label
                  caption
                  v-if="getDraftPlayerAtPosition(pos)"
                  :class="{ 'text-positive': isPositionChanged(pos) }"
                >
                  {{ getDraftPlayerAtPosition(pos)?.name || 'Empty' }}
                </q-item-label>
              </q-item-section>

              <q-item-section side>
                <q-btn flat round dense icon="swap_horiz" color="grey-7" />
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>

        <!-- Sticky Footer for Actions -->
        <q-card-actions align="between" class="col-auto bg-white border-top q-pa-md z-top">
          <q-btn flat label="Discard" color="negative" v-close-popup />
          <q-btn
            unelevated
            label="Save Defense"
            color="primary"
            icon-right="check"
            :disable="!hasUnsavedChanges"
            @click="commitDefenseChanges"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- BOTTOM SHEET 2: ROSTER SELECTION -->
    <q-dialog v-model="showSwapDialog" position="bottom">
      <q-card
        class="full-width bg-white column no-wrap"
        style="max-height: 65vh; border-radius: 16px 16px 0 0"
      >
        <q-card-section
          class="row items-center justify-between shrink bg-grey-2 border-bottom z-top"
        >
          <div class="text-subtitle1 text-weight-bold">
            Select For {{ settingsStore.getPositionShortLabel(activeSwapPosition || 0) }}
          </div>
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section class="col-grow scroll q-pa-none">
          <q-list separator>
            <q-item
              v-for="player in availableDefensivePlayers"
              :key="'swap-' + player.id"
              clickable
              v-ripple
              @click="executeLocalSwap(player.id)"
            >
              <q-item-section>
                <q-item-label
                  :class="{ 'text-primary text-weight-bold': player.label.includes('(Current)') }"
                >
                  {{ player.label }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import { useQuasar } from 'quasar'
import { useGameStore } from 'stores/game-store'
import { useSettingsStore } from 'stores/settings-store'
import GameTrackerPitching from 'components/GameTrackerPitching.vue'
import GameTrackerHitting from 'components/GameTrackerHitting.vue'
import GameTrackerAction from 'components/GameTrackerAction.vue'
import GameTrackerScouting from 'components/GameTrackerScouting.vue'
import GameAdjustmentSheet from 'components/GameAdjustmentSheet.vue'
import GameTrackerSummary from 'components/GameTrackerSummary.vue'
import HelpHint from 'components/HelpHint.vue'
import type { DefenseSnapshot, LineupEntry, Position } from 'stores/base-types'

const $q = useQuasar()
const gameStore = useGameStore()
const settingsStore = useSettingsStore()

// -- INTERNAL STATE --
const showAdjustmentSheet = ref(false)
const showDefenseSheet = ref(false)
const showSwapDialog = ref(false)
const activeSwapPosition = ref<number | null>(null)

// NEW: Local draft state
const draftDefense = ref<DefenseSnapshot>({})
const originalDefense = ref<DefenseSnapshot>({})

const refPitchComponent = ref<InstanceType<typeof GameTrackerPitching> | null>(null)
const activeView = ref<string>('pitch')
const editingPitchId = ref<string | number | null>(null)

// -- BATTER / ACTION LOGIC --
const isLiveEdge = computed(
  () => gameStore.currentPAIndex === gameStore.plateAppearances.length - 1,
)

const basesEmpty = computed(() => {
  const br = gameStore.currentBaseRunners
  return br['1'].length === 0 && br['2'].length === 0 && br['3'].length === 0
})

const responsiveToggleOptions = computed(() => {
  const isMobile = $q.screen.xs // True on typical mobile phones

  return [
    {
      label: isMobile ? '' : 'Pitching',
      icon: 'sports_baseball',
      value: 'pitch',
      disable: false,
    },
    {
      label: isMobile ? '' : 'Hitting',
      icon: 'sports_cricket', // Or any bat-like icon
      value: 'hit',
      //disable: !gameStore.currentPitchIsHit,
    },
    {
      label: isMobile ? '' : 'Play',
      icon: 'directions_run',
      value: 'play',
      disable: !gameStore.currentPitchIsHit && basesEmpty.value,
    },
    {
      label: isMobile ? '' : 'Summary',
      icon: 'list_alt',
      value: 'summary',
      disable: false,
    },
    {
      label: isMobile ? '' : 'Scouting',
      icon: 'remove_red_eye',
      value: 'scout',
      disable: false,
    },
  ]
})

function syncTogglesToLastPitch() {
  refPitchComponent.value?.syncTogglesToLastPitch()
}
const previousBatter = async () => {
  gameStore.previousBatter()
  activeView.value = 'pitch'
  await nextTick()
  syncTogglesToLastPitch()
}

const nextBatter = async () => {
  if (isLiveEdge.value && !gameStore.currentAbState(true).status) return

  await gameStore.nextBatter()
  editingPitchId.value = null
  activeView.value = 'pitch'
  await nextTick()
  syncTogglesToLastPitch()
}

// -- DEFENSE & DRAFT SUBSTITUTION LOGIC --

// 1. Initialize draft when opening the sheet
const openDefenseSheet = () => {
  originalDefense.value = { ...gameStore.currentDefenseSnapshot }
  draftDefense.value = { ...gameStore.currentDefenseSnapshot }
  showDefenseSheet.value = true
}

const hasUnsavedChanges = computed(() => {
  return JSON.stringify(draftDefense.value) !== JSON.stringify(originalDefense.value)
})

const isPositionChanged = (pos: number) => {
  return draftDefense.value[pos as Position] !== originalDefense.value[pos as Position]
}

// 2. Lookup players from the DRAFT, not the store directly
const getDraftPlayerAtPosition = (pos: number) => {
  const playerId = draftDefense.value[pos as Position]?.playerId
  if (!playerId) return null

  const currentPA = gameStore.viewedPlateAppearance
  const lineup = currentPA?.halfInning === 'top' ? gameStore.homeLineup : gameStore.visitingLineup
  if (!lineup) return null

  const allPlayers = lineup.concat(lineup.flatMap((l) => l.substitutions || []))
  const lineupEntry = allPlayers.find((p) => p.player.id === playerId)

  return lineupEntry ? lineupEntry.player : null
}

const openSwapMenu = (pos: number) => {
  activeSwapPosition.value = pos
  showSwapDialog.value = true
}

// 3. Available players check the DRAFT for their current positions
const availableDefensivePlayers = computed(() => {
  const currentPA = gameStore.viewedPlateAppearance
  if (!currentPA) return []

  const lineup = currentPA.halfInning === 'top' ? gameStore.homeLineup : gameStore.visitingLineup

  return (
    lineup?.map((e) => {
      let activePos = null
      for (const [posStr, id] of Object.entries(draftDefense.value)) {
        if (id.playerId === e.player.id) {
          activePos = Number(posStr)
          break
        }
      }

      const posLabel = activePos
        ? `- Pos ${settingsStore.getPositionShortLabel(activePos)}`
        : '- Bench'
      const isCurrent = activePos === activeSwapPosition.value ? '(Current)' : ''

      return {
        label: `#${e.player.uniformNumber || '-'} ${e.player.name} ${posLabel} ${isCurrent}`.trim(),
        id: e.player.id,
      }
    }) || []
  )
})

// 4. Mutate the DRAFT locally
const executeLocalSwap = (playerId?: number) => {
  if (!playerId || !activeSwapPosition.value) return

  // Scrub the player from their old position if they are already on the field
  for (const [posStr, id] of Object.entries(draftDefense.value)) {
    if (id.playerId === playerId) {
      delete draftDefense.value[Number(posStr) as Position]
    }
  }

  // Assign them to the new position
  draftDefense.value[activeSwapPosition.value as Position] = {
    ...draftDefense.value[activeSwapPosition.value as Position],
    playerId: playerId,
  }

  // Close the swap roster dialog, returning to the draft sheet
  showSwapDialog.value = false
}

// 5. Compare old and new to build a clean string
const buildDefensiveSwapSummary = (
  oldSnap: DefenseSnapshot,
  newSnap: DefenseSnapshot,
  allPlayers: LineupEntry[],
): string => {
  const changes: string[] = []
  const posNames: Record<number, string> = {
    1: 'P',
    2: 'C',
    3: '1B',
    4: '2B',
    5: '3B',
    6: 'SS',
    7: 'LF',
    8: 'CF',
    9: 'RF',
  }

  for (let pos = 1; pos <= 9; pos++) {
    const oldPlayer = oldSnap[pos as Position]?.playerId
    const newPlayer = newSnap[pos as Position]?.playerId

    if (newPlayer && newPlayer !== oldPlayer) {
      const playerEntry = allPlayers.find((p) => p.player.id === newPlayer)
      const name = playerEntry ? `#${playerEntry.player.uniformNumber || '-'}` : 'Unknown'
      changes.push(`${name} to ${posNames[pos]}`)
    }
  }
  return changes.length > 0
    ? `Defensive shifts: ${changes.join(', ')}.`
    : 'Defensive alignments updated.'
}

// 6. Commit the Draft!
const commitDefenseChanges = async () => {
  const currentPA = gameStore.viewedPlateAppearance
  const lineup = currentPA?.halfInning === 'top' ? gameStore.homeLineup : gameStore.visitingLineup
  const allPlayers = lineup ? lineup.concat(lineup.flatMap((l) => l.substitutions || [])) : []

  const summaryNotes = buildDefensiveSwapSummary(
    originalDefense.value,
    draftDefense.value,
    allPlayers,
  )

  // Pass it to the store action we are about to create!
  await gameStore.applyDefensiveChanges(draftDefense.value, summaryNotes)

  showDefenseSheet.value = false
}
</script>

<style scoped>
:deep(.view-toggle .q-btn-group) {
  gap: 4px;
}
.z-top {
  z-index: 10;
}
.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}
.border-top {
  border-top: 1px solid #e0e0e0;
}
</style>
